import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Segmented } from "../../../components/ui";
import { apiMessage, createAdmin, updateAdmin } from "../../../services/admins";
import { can } from "../../../utils/roles";
import "./admin-form.css";

export const ADMIN_ROLES = [
  "content admin",
  "analytics admin",
  "marketing admin",
  "finance admin",
  "user admin",
  "support admin",
];

/** Translation keys of the permission sections and groups (API keys). */
export const SECTION_LABELS = {
  dashboard: "Dashboard",
  analytics: "Analytics",
  videos: "Videos",
  series: "Series and episodes",
  podcasts: "Podcasts",
  categories: "Categories",
  channels: "Live TV channels",
  sliders: "Sliders",
  ads: "Ads",
  notifications: "Notifications",
  broadcast: "Broadcast",
  users: "Users",
  admins: "Administrators",
  transactions: "Payments (donations, subscriptions, purchases)",
  payments: "Payment Gateway",
  plans: "Subscription plan",
  logs: "Logs and failed payments",
  faqs: "FAQS",
  legal: "Legal Information",
  app_menu: "Main Menu",
  mobile_app: "Mobile app",
  quiz: "Game (quiz)",
};
const GROUP_LABELS = {
  overview: "Overview",
  content: "Content",
  marketing: "Marketing",
  people: "People",
  finance: "Finance",
  site: "Site and apps",
};

const LEVELS = ["none", "view", "edit"];
const rank = (level) => Math.max(0, LEVELS.indexOf(level));

/** Permissions given by roles: union of the role presets of the catalog. */
export function presetFor(roles, catalog) {
  const result = {};
  for (const section of catalog?.sections || []) result[section.key] = "none";
  for (const role of roles) {
    for (const [key, level] of Object.entries(catalog?.presets?.[role] || {})) {
      if (rank(level) > rank(result[key])) result[key] = level;
    }
  }
  return result;
}

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  password: "",
  status: "active",
  role: [],
};
const toastStyle = { progressStyle: { background: "var(--iq-primary)" } };

const hasSpecialCharacters = (password) =>
  /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password);

const roleLabel = (role) => role.replace(/\b\w/g, (c) => c.toUpperCase());

/** Per-section access grid. Read-only when `onChange` is missing. */
const AccessGrid = ({ catalog, permissions, onChange }) => {
  const { t } = useTranslation();
  const groups = useMemo(() => {
    const byGroup = new Map();
    for (const section of catalog.sections) {
      if (!byGroup.has(section.group)) byGroup.set(section.group, []);
      byGroup.get(section.group).push(section);
    }
    return [...byGroup.entries()];
  }, [catalog]);

  return (
    <div className="admin-access">
      {groups.map(([group, sections]) => (
        <div key={group} className="admin-access-group">
          <span className="ui-label">{t(GROUP_LABELS[group] || group)}</span>
          {sections.map((section) => {
            const label = t(SECTION_LABELS[section.key] || section.label);
            const single = section.levels.length === 1;
            const options = ["none", ...section.levels].map((level) => ({
              value: level,
              label:
                level === "none"
                  ? t("No access")
                  : single
                  ? t("Access")
                  : level === "view"
                  ? t("Read only")
                  : t("Full access"),
              // Nobody can give more than they have (checked again by the API).
              disabled: !onChange || (level !== "none" && !can(section.key, level)),
              title:
                onChange && level !== "none" && !can(section.key, level)
                  ? t("You don't have this access yourself")
                  : undefined,
            }));
            return (
              <div key={section.key} className="admin-access-row">
                <span className="admin-access-name">{label}</span>
                <Segmented
                  label={label}
                  value={permissions[section.key] || "none"}
                  onChange={(level) => onChange?.(section.key, level)}
                  options={options}
                />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

/**
 * Create / edit an admin. `admin` is null to create one. `catalog`: sections,
 * roles and role presets from the API. Calls `onSaved()` after a successful
 * request.
 */
const AdminFormModal = ({ admin, assignableRoles, catalog, onClose, onSaved }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(admin);
  const [form, setForm] = useState(EMPTY_FORM);
  const [custom, setCustom] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [passwordWarning, setPasswordWarning] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(
      admin
        ? {
            name: admin.name || "",
            email: admin.email || "",
            phone: admin.phone || "",
            password: "",
            status: admin.status === "inactive" ? "inactive" : "active",
            role: admin.role || [],
          }
        : EMPTY_FORM
    );
    setCustom(Boolean(admin?.permissions));
    setPermissions(admin?.permissions || {});
  }, [admin]);

  const superuserRoles = catalog?.superuserRoles || [];
  const fullAccess = form.role.some((role) => superuserRoles.includes(role));
  const rolePermissions = useMemo(
    () => presetFor(form.role, catalog),
    [form.role, catalog]
  );

  const set = (field) => (value) =>
    setForm((previous) => ({ ...previous, [field]: value }));
  const toggleRole = (role) =>
    set("role")(
      form.role.includes(role)
        ? form.role.filter((r) => r !== role)
        : [...form.role, role]
    );
  const switchCustom = (value) => {
    // Custom access starts from what the roles give.
    if (value && !custom) setPermissions(rolePermissions);
    setCustom(value);
  };

  const handlePasswordBlur = () => {
    if (
      form.password &&
      (!hasSpecialCharacters(form.password) || form.password.length < 9)
    ) {
      setPasswordWarning(true);
      setTimeout(() => setPasswordWarning(false), 3000);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.role.length) {
      toast.error(t("Please fill in all the fields completely"), toastStyle);
      return;
    }
    if (form.password && form.password.length <= 8) {
      toast.error(t("Password should be at least 9 characters long"), toastStyle);
      return;
    }
    const payload = {
      ...form,
      permissions: custom && !fullAccess ? permissions : null,
    };
    // When editing, an empty password keeps the current one.
    if (!payload.password) delete payload.password;

    setSaving(true);
    try {
      if (isEdit) {
        await updateAdmin(payload, admin._id);
        toast.success(t("Admin Info Updated Successfully!"), toastStyle);
      } else {
        const response = await createAdmin(payload);
        // The API answers with an error message instead of the created admin.
        if (!response?.savedAdmin) throw new Error(response);
        toast.success(t("Admin Info Submitted Successfully!"), toastStyle);
        onSaved(response);
        return;
      }
      onSaved();
    } catch (error) {
      const status = error?.response?.status;
      const message = apiMessage(error);
      toast.error(
        status === 400 && /exist/i.test(message)
          ? t("Email Already Exists")
          : message || t("Error updating data"),
        toastStyle
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={true}
      onHide={onClose}
      centered
      size="lg"
      dialogClassName="ui-modal"
    >
      <form onSubmit={handleSubmit}>
        <Modal.Header>
          <Modal.Title>{isEdit ? t("Edit admin") : t("Add Admin")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ui-form">
            <div className="ui-field">
              <label htmlFor="admin-name">{t("Admin Name")}</label>
              <input
                id="admin-name"
                className="ui-input"
                value={form.name}
                onChange={(e) => set("name")(e.target.value)}
              />
            </div>
            <div className="ui-field">
              <label htmlFor="admin-email">{t("email")}</label>
              <input
                id="admin-email"
                type="email"
                className="ui-input"
                placeholder="youremail@example.com"
                value={form.email}
                disabled={isEdit}
                onChange={(e) => set("email")(e.target.value)}
              />
            </div>
            <div className="ui-field">
              <label htmlFor="admin-phone">{t("Phone Number")}</label>
              <input
                id="admin-phone"
                type="tel"
                className="ui-input"
                value={form.phone}
                onChange={(e) =>
                  set("phone")(e.target.value.replace(/\D/g, ""))
                }
              />
            </div>
            <div className="ui-field">
              <label htmlFor="admin-password">{t("Password")}</label>
              <div className="ui-input-group">
                <input
                  id="admin-password"
                  type={showPassword ? "text" : "password"}
                  className="ui-input"
                  placeholder={
                    isEdit
                      ? t("Leave empty to keep the current one")
                      : t("Leave empty: a temporary password is generated")
                  }
                  value={form.password}
                  onBlur={handlePasswordBlur}
                  onChange={(e) => set("password")(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="ui-input-addon"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={
                    showPassword ? t("Hide password") : t("Show password")
                  }
                >
                  <i
                    className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}
                    aria-hidden="true"
                  ></i>
                </button>
              </div>
              <p
                className="ui-hint"
                data-error={passwordWarning ? "" : undefined}
              >
                {isEdit
                  ? t("The admin can change this password at any time from My profile.")
                  : t("The admin receives the sign-in details by email and can choose their own password at any time.")}
              </p>
            </div>
            <div className="ui-field ui-field-full">
              <span className="ui-label">{t("status")}</span>
              <div className="ui-chips">
                {["active", "inactive"].map((status) => (
                  <button
                    key={status}
                    type="button"
                    className="ui-chip"
                    aria-pressed={form.status === status}
                    onClick={() => set("status")(status)}
                  >
                    {t(status === "active" ? "Active" : "Inactive")}
                  </button>
                ))}
              </div>
            </div>
            <div className="ui-field ui-field-full">
              <span className="ui-label">{t("Roles")}</span>
              <div className="ui-chips">
                {assignableRoles.map((role) => (
                  <button
                    key={role}
                    type="button"
                    className="ui-chip"
                    aria-pressed={form.role.includes(role)}
                    onClick={() => toggleRole(role)}
                  >
                    {t(roleLabel(role))}
                  </button>
                ))}
              </div>
              <p className="ui-hint">
                {t("A role is a ready-made set of accesses. You can then adjust them one by one.")}
              </p>
            </div>

            <div className="ui-field ui-field-full">
              <div className="admin-access-head">
                <span className="ui-label">{t("Access to the tabs")}</span>
                {!fullAccess && catalog && (
                  <Segmented
                    label={t("Access to the tabs")}
                    value={custom ? "custom" : "roles"}
                    onChange={(value) => switchCustom(value === "custom")}
                    options={[
                      { value: "roles", label: t("From the roles") },
                      { value: "custom", label: t("Custom") },
                    ]}
                  />
                )}
              </div>
              {!catalog ? (
                <p className="ui-hint">{t("Loading…")}</p>
              ) : fullAccess ? (
                <p className="ui-hint">
                  {t("System and super admins have access to every tab.")}
                </p>
              ) : (
                <>
                  <p className="ui-hint">
                    {custom
                      ? t("Custom accesses replace those of the roles. Read only: the tab is visible, nothing can be changed.")
                      : t("Accesses given by the selected roles. Choose “Custom” to adjust them.")}
                  </p>
                  <AccessGrid
                    catalog={catalog}
                    permissions={custom ? permissions : rolePermissions}
                    onChange={
                      custom
                        ? (key, level) =>
                            setPermissions((current) => ({ ...current, [key]: level }))
                        : undefined
                    }
                  />
                </>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="ui-btn ui-btn-secondary"
            onClick={onClose}
          >
            {t("Cancel")}
          </button>
          <button type="submit" className="ui-btn" disabled={saving}>
            {saving ? t("Saving…") : isEdit ? t("Update") : t("submit")}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default AdminFormModal;
